import React, { useCallback, useMemo, useRef } from 'react';
import MapBox from './MapBox';

import Bottombar from './Bottombar';
import Layers from './Layers';
import Modal from './Modal';
import Sidebar from './Sidebar';
import UrbanizeLayers from './UrbanizeLayers';
import UrbanizeSidebar from './UrbanizeSidebar';

import { debounce } from 'lodash';
import proj4 from 'proj4';
import RBush from 'rbush';
import { doPolyFeaturesOverlapAtAll, flattenFeatures, getFirstCoord, getProjected, makeBBoxCoords, proj4String } from './geo';

const DEBOUNCE_SEC = 2000;

function Urbanize({ data, selection, setSelection, open, setOpen }) {
  const dataUpdated = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const lat = urlParams.get('lat');
    const lon = urlParams.get('lon');
    data.properties = {
      columns: [
        'name',
        'urb_address',
        'urb_project_status',
        'urb_developer',
        'urb_architect',
        'urb_project_uses',
        'urb_num_apts',
        'urb_num_parking',
        'urb_retail_space_sf'
      ],
      // center:   [ -118.25827769, 34.04491342 ] // LA
      center: lat && lon ? [lon, lat] : [-118.25827769, 34.04491342] // LA
    };
    return data;
  }, [data]);

  const updateSource = useRef(
    debounce(
      (map, data) => {
        // const fs = flattenFeatures(map.queryRenderedFeatures({
        //   layers: ['building-extrusion'],
        // }));
        const fs = flattenFeatures(
          map.querySourceFeatures('composite', {
            sourceLayer: 'building'
          })
        );

        // evt.sourceId == 'composite'
        // evt.isSourceLoaded
        const proj = proj4(proj4String(getFirstCoord(data.features[0])));
        // giraffe buildings
        data.features.forEach(f => {
          f._projected = getProjected(f, proj);
          f.bbox = makeBBoxCoords(f._projected);
        });

        const fsById = {};
        // 3D context buildings
        fs.forEach((f, fidx) => {
          f._projected = getProjected(f, proj);
          f.bbox = makeBBoxCoords(f._projected, { id: f.id });
          fsById[f.id] = fidx;
        });

        // TODO not just bbox intersection
        // note doPolyFeaturesOverlapAtAll wants _projected to be computed
        // const ids = fs.filter(f => {
        //   return data.features.filter(f1 => f1.geometry.type === 'Polygon').some(f1 => doPolyFeaturesOverlapAtAll(f, f1));
        // }).map(f => f.id);
        const contextBush = new RBush();
        contextBush.load(fs.map(f => f.bbox));
        const ids = [];
        data.features.forEach(f => {
          if (f.geometry.type !== 'Polygon') return;
          const bbox = f.bbox;
          const matches = contextBush.search(bbox);
          matches.forEach(m => {
            if (doPolyFeaturesOverlapAtAll(f, fs[fsById[m.id]])) {
              ids.push(m.id);
            }
          });
        });

        // unfortunately layer doesn't have IDs for some zoom levels
        map.setFilter('building-extrusion', ['!in', '$id', ...ids]);
      },
      DEBOUNCE_SEC,
      { leading: true }
    )
  );

  const cb = useCallback(
    map => {
      const { lng, lat } = map.getCenter();

      if (data.features.length == 0) return;
      map.on('sourcedata', evt => {
        if (evt.sourceId != 'composite') return;
        if (!evt.isSourceLoaded) return;
        updateSource.current(map, data);
      });
    },
    [data]
  );

  return (
    <React.Fragment>
      <link href="https://la.urbanize.city/themes/custom/urbanize_2018/css/giraffe-map.css" rel="stylesheet" />
      {/* <Modal open={open} setOpen={setOpen} /> */}
      <UrbanizeSidebar data={dataUpdated} selection={selection} setSelection={setSelection} setOpen={() => {}} />
      {data.features.length > 0 && (
        <MapBox
          center={dataUpdated.properties.center}
          zoom={16}
          pitch={45}
          style={'mapbox://styles/mikey-brisbanedevelopment/cl5fwr45l009w15sbrtt67fct'}
          layers={UrbanizeLayers({ data: dataUpdated })}
          selection={selection}
          setSelection={setSelection}
          cb={cb}
        />
      )}
      <Bottombar />
    </React.Fragment>
  );
}

function App() {
  const [selection, setSelection] = React.useState([]);
  const parsedUrl = new URL(window.location.href);
  const projectId = parsedUrl.pathname.split('/')[1];
  const [data, setData] = React.useState({ type: 'FeatureCollection', features: [], properties: { columns: [] } });
  // modalOpen

  const [open, setOpen] = React.useState(undefined);

  React.useEffect(() => {
    fetch(`https://storage.googleapis.com/city-app/${projectId}.json`)
      .then(r => r.json())
      .then(r => setData(r));
  }, [projectId]);

  if (projectId == 'urbanize') {
    return <Urbanize data={data} selection={selection} setSelection={setSelection} open={open} setOpen={setOpen} />;
  }

  return (
    <React.Fragment>
      <Modal open={open} setOpen={setOpen} />
      <Sidebar data={data} selection={selection} setSelection={setSelection} setOpen={setOpen} />
      {data.features.length > 0 && <MapBox center={data.properties.center} layers={Layers({ data })} selection={selection} setSelection={setSelection} />}
      <Bottombar />
    </React.Fragment>
  );
}

export default App;
